/**
 * Roles management — API-backed; permissions from DB via DbPermissionEditor
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Tooltip,
  Drawer,
  Badge,
  Select,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { SmartBreadcrumbs } from '@/components/Breadcrumbs';
import { DbPermissionEditor } from '@/components/permissions/DbPermissionEditor';
import roleService, { Role, RoleStatistics } from '@/services/role.service';
import { useAuthStore } from '@/store/authStore';

const { Title, Text } = Typography;
const { Search, TextArea } = Input;

type RoleRow = Role & {
  _stats?: RoleStatistics;
};

export const RolesPage: React.FC = () => {
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  const refreshSession = useAuthStore((s) => s.refreshSession);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [cloneModalVisible, setCloneModalVisible] = useState(false);
  const [permissionsDrawerVisible, setPermissionsDrawerVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleRow | null>(null);
  const [selectedRole, setSelectedRole] = useState<RoleRow | null>(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const [cloneForm] = Form.useForm();

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const list = await roleService.getRoles({
        is_active: true,
        company_id: authUser?.company_id || undefined,
      });
      const arr = Array.isArray(list) ? list : [];
      const withStats = await Promise.all(
        arr.map(async (r) => {
          try {
            const stats = await roleService.getRoleStatistics(r.id);
            return { ...r, _stats: stats };
          } catch {
            return { ...r, _stats: undefined };
          }
        })
      );
      setRoles(withStats);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to load roles');
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [authUser?.company_id]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const templateRoles = useMemo(
    () => roles.filter((r) => r.is_system_role),
    [roles]
  );

  const filteredRoles = useMemo(() => {
    const q = searchText.toLowerCase();
    if (!q) return roles;
    return roles.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.code.toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q)
    );
  }, [roles, searchText]);

  const handleSaveRole = async (values: any) => {
    try {
      setLoading(true);
      if (editingRole) {
        await roleService.updateRole(editingRole.id, {
          name: values.name,
          description: values.description,
        });
        message.success('Role updated');
      } else {
        const company_id = authUser?.company_id;
        if (!company_id && !authUser?.is_super_admin) {
          message.error('Company context required');
          return;
        }
        await roleService.createRole({
          name: values.name,
          code: values.code.trim().toLowerCase().replace(/\s+/g, '-'),
          description: values.description,
          company_id: (values.company_id || company_id) as string | undefined,
        });
        message.success('Role created — assign permissions next');
      }
      setModalVisible(false);
      setEditingRole(null);
      form.resetFields();
      loadRoles();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    try {
      setLoading(true);
      await roleService.deleteRole(roleId);
      message.success('Role deleted');
      loadRoles();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const submitClone = async () => {
    try {
      const v = await cloneForm.validateFields();
      setLoading(true);
      await roleService.cloneRole(v.source_role_id, {
        name: v.name,
        code: v.code.trim().toLowerCase().replace(/\s+/g, '-'),
        description: v.description,
        company_id: v.company_id || authUser?.company_id,
      });
      message.success('Role cloned');
      setCloneModalVisible(false);
      cloneForm.resetFields();
      loadRoles();
    } catch (e: any) {
      if (!e?.errorFields) {
        message.error(e?.response?.data?.message || 'Clone failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingRole(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (role: RoleRow) => {
    if (role.is_system_role) {
      message.warning('Edit the clone of a template role, not the template itself');
      return;
    }
    setEditingRole(role);
    form.setFieldsValue({
      name: role.name,
      description: role.description,
    });
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'Role',
      key: 'name',
      width: 240,
      render: (_: unknown, r: RoleRow) => (
        <Space>
          <SafetyCertificateOutlined style={{ color: '#145A54' }} />
          <Text strong>{r.name}</Text>
          {r.is_system_role && <Tag color="gold">Template</Tag>}
        </Space>
      ),
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 180,
      render: (t: string) => <Tag>{t}</Tag>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Permissions',
      key: 'pc',
      width: 110,
      align: 'center' as const,
      render: (_: unknown, r: RoleRow) => (
        <Badge
          count={r._stats?.permissionCount ?? 0}
          showZero
          style={{ backgroundColor: '#52c41a' }}
        />
      ),
    },
    {
      title: 'Users',
      key: 'uc',
      width: 90,
      align: 'center' as const,
      render: (_: unknown, r: RoleRow) => (
        <Space>
          <UserOutlined />
          {r._stats?.userCount ?? 0}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      fixed: 'right' as const,
      render: (_: unknown, r: RoleRow) => (
        <Space>
          <Button
            type="link"
            icon={<SafetyCertificateOutlined />}
            onClick={() => {
              setSelectedRole(r);
              setPermissionsDrawerVisible(true);
            }}
          >
            Permissions
          </Button>
          {!r.is_system_role && (
            <>
              <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(r)} />
              <Button
                type="link"
                icon={<CopyOutlined />}
                onClick={() => {
                  cloneForm.setFieldsValue({
                    source_role_id: r.id,
                    name: `${r.name} (copy)`,
                    code: `${r.code}-copy`,
                    description: r.description,
                  });
                  setCloneModalVisible(true);
                }}
              />
              <Popconfirm
                title="Delete role?"
                description={
                  (r._stats?.userCount ?? 0) > 0
                    ? 'Remove users from this role first'
                    : undefined
                }
                disabled={(r._stats?.userCount ?? 0) > 0}
                onConfirm={() => handleDeleteRole(r.id)}
              >
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={(r._stats?.userCount ?? 0) > 0}
                />
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <SmartBreadcrumbs />

      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/platform/security/users')}
        style={{ padding: 0, marginBottom: 16 }}
      >
        Back to users
      </Button>

      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <SafetyCertificateOutlined /> Roles
            </Title>
            <Text type="secondary">
              Templates are read-only — clone to customize for your company
            </Text>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<CopyOutlined />}
                onClick={() => {
                  cloneForm.resetFields();
                  setCloneModalVisible(true);
                }}
                disabled={!templateRoles.length}
              >
                Clone from template
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                Create role
              </Button>
            </Space>
          </Col>
        </Row>

        <Row style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Search
              placeholder="Search roles"
              allowClear
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
        </Row>

        <Table<RoleRow>
          columns={columns as any}
          dataSource={filteredRoles}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{ defaultPageSize: 12, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: (t) => `${t} roles` }}
        />
      </Card>

      <Modal
        title={editingRole ? 'Edit role' : 'Create role'}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalVisible(false);
          setEditingRole(null);
          form.resetFields();
        }}
        width={560}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSaveRole}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, min: 2 }]}
          >
            <Input placeholder="e.g. Production supervisor" />
          </Form.Item>
          {!editingRole && (
            <Form.Item
              name="code"
              label="Code"
              rules={[{ required: true, pattern: /^[a-z0-9-]+$/ }]}
            >
              <Input placeholder="e.g. mfg-supervisor-acme" />
            </Form.Item>
          )}
          <Form.Item name="description" label="Description">
            <TextArea rows={3} />
          </Form.Item>
          {authUser?.is_super_admin && !editingRole && (
            <Form.Item name="company_id" label="Company ID (optional)">
              <Input placeholder="Defaults to your company if empty" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title="Clone from template or role"
        open={cloneModalVisible}
        onOk={submitClone}
        onCancel={() => {
          setCloneModalVisible(false);
          cloneForm.resetFields();
        }}
        confirmLoading={loading}
        destroyOnClose
        width={520}
      >
        <Form form={cloneForm} layout="vertical">
          <Form.Item
            name="source_role_id"
            label="Source role"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Pick template or existing role"
              options={roles.map((r) => ({
                value: r.id,
                label: `${r.name} (${r.code})${r.is_system_role ? ' [template]' : ''}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="name" label="New role name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="code"
            label="New code"
            rules={[{ required: true, pattern: /^[a-z0-9-]+$/ }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} />
          </Form.Item>
          {authUser?.is_super_admin && !authUser?.company_id && (
            <Form.Item
              name="company_id"
              label="Company ID for cloned role"
              rules={[{ required: true, message: 'Company UUID required' }]}
            >
              <Input placeholder="Target company UUID" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Drawer
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span>{selectedRole?.name} — permissions</span>
          </Space>
        }
        width={720}
        open={permissionsDrawerVisible}
        onClose={() => {
          setPermissionsDrawerVisible(false);
          setSelectedRole(null);
        }}
        destroyOnClose
      >
        {selectedRole && (
          <DbPermissionEditor
            roleId={selectedRole.id}
            roleName={selectedRole.name}
            isSystemRole={selectedRole.is_system_role}
            onSaved={async () => {
              try {
                await refreshSession();
                message.success(
                  'Permissions saved — your session was refreshed so the sidebar uses the new permissions.'
                );
              } catch {
                message.warning(
                  'Permissions saved. Refresh the page or sign out and sign in so menus pick up the changes.'
                );
              }
              loadRoles();
            }}
          />
        )}
      </Drawer>
    </div>
  );
};

export default RolesPage;
